/**
 * Platform ops status for the Studio Admin Platform page, polled from the
 * API's cached snapshot.
 *
 * It used to hold an SSE stream open. The API only speaks HTTP/1.1, so a
 * browser has about six connections to it for every tab, and each open Studio
 * tab already keeps one for its event stream; one more long-lived connection
 * here made every other admin request queue. A short request every few seconds
 * holds a connection for milliseconds instead.
 *
 * - Polls every few seconds while the tab is visible, and at once when it
 *   comes back; never two requests at a time.
 * - Keeps the last good snapshot on a failed poll and backs off.
 * - Paints the last snapshot this browser saw straight away while the first
 *   poll is in flight.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const BASE_URL = import.meta.env.VITE_STUDIO_API_BASE_URL || 'http://localhost:8087';
export const OPS_POLL_MS = 3000;
const MAX_BACKOFF_MS = 30000;
const REQUEST_TIMEOUT_MS = 10000;
const CACHE_KEY = 'studio-admin.ops-snapshot';
/** An older cached snapshot is misleading rather than helpful on first paint. */
const CACHE_MAX_AGE_MS = 10 * 60 * 1000;

let getAccessToken: (() => Promise<string>) | null = null;

export function setOpsAccessTokenProvider(provider: () => Promise<string>) {
  getAccessToken = provider;
}

/** When to poll next: the steady interval, or a backoff that doubles with each consecutive failure. */
export function nextPollDelay(consecutiveFailures: number): number {
  if (consecutiveFailures <= 0) return OPS_POLL_MS;
  return Math.min(MAX_BACKOFF_MS, OPS_POLL_MS * 2 ** (consecutiveFailures - 1));
}

export interface LatencyStats {
  samples: number;
  p50Ms: number | null;
  p95Ms: number | null;
  maxMs: number | null;
}

export interface OpsSnapshot {
  generatedAt: string;
  sandbox: {
    workspaces: number;
    byPhase: Record<string, number>;
    byProvider: Record<string, number>;
    openToPreview: Record<string, LatencyStats>;
    /** Absent from an API that predates it. */
    openToPreviewByProvider?: Record<string, Record<string, LatencyStats>>;
    previewSignals?: { vite: LatencyStats; appService: LatencyStats };
    recentFailures: Array<{ scopeHash: string; generation: number; provider?: string; code: string; at: string }>;
    failuresByProvider?: Record<string, Record<string, number>>;
    tenants: Array<{ tenantId: string; organizationName: string; active: number; limit: number | null }>;
    lanes?: Record<string, { active: number }>;
  };
  /** Absent from an API that predates it; null when the MicroVM lane is not configured. */
  microvm?: MicrovmFleet | null;
  /** Absent from an API that predates it; null when there is no lifecycle log to read. */
  workspaceMinutes?: WorkspaceMinutes | null;
}

export interface WorkspaceMinutesTotals {
  workspaces: number;
  runningMs: number;
  activeMs: number;
  idleMs: number;
  /** Only when the API has a MicroVM price configured. */
  costUsd?: number;
}
export interface WorkspaceMinutesWindow extends WorkspaceMinutesTotals {
  from: string;
  to: string;
  services: { vms: number; runningMs: number; costUsd?: number };
  byOrganization: Array<WorkspaceMinutesTotals & { organizationId?: string; organizationName?: string }>;
}
export interface WorkspaceMinutes {
  generatedAt: string;
  pricePerHourUsd?: number;
  truncated: boolean;
  today: WorkspaceMinutesWindow;
  last7Days: WorkspaceMinutesWindow;
}

export type MicrovmState = 'PENDING' | 'RUNNING' | 'SUSPENDING' | 'SUSPENDED' | 'TERMINATING' | 'TERMINATED';
export interface MicrovmFleetVm {
  microvmId: string;
  state: MicrovmState;
  imageVersion?: string;
  startedAt?: string;
  maximumDurationSeconds?: number;
  stateReason?: string;
  owned: boolean;
  phase?: string;
  generation?: number;
  sessionId?: string;
  projectId?: string;
  organizationId?: string;
  organizationName?: string;
  suspendedAt?: string;
  lastActivityAt?: string;
  lastSnapshot?: { uri: string; bytes: number; at: string };
  lastError?: { code: string; message: string; at: string };
  lastRebound?: { path: string; claimWaitMs?: number; readyAt?: string };
}
export interface MicrovmFleetEvent {
  at: string;
  type: string;
  microvmId: string;
  sessionId?: string;
  projectId?: string;
  organizationId?: string;
  organizationName?: string;
  runId?: string;
  imageVersion?: string;
  reason?: string;
  durationMs?: number;
  idleMs?: number;
}
export interface MicrovmFleet {
  configured: boolean;
  observedAt: string | null;
  ageMs: number | null;
  stale: boolean;
  image: { arn?: string; activeVersion?: string; pinnedVersion?: string };
  counts: Record<MicrovmState, number>;
  live: number;
  orphans: number;
  byImageVersion: Array<{ version: string; count: number; active: boolean }>;
  rowPhases: Record<string, number>;
  lastHour: Record<string, number>;
  truncated: boolean;
  vms: MicrovmFleetVm[];
  recent: MicrovmFleetEvent[];
}

export type ConnectionStatus = 'connecting' | 'live' | 'reconnecting' | 'offline';

export interface UseOpsSnapshotResult {
  snapshot: OpsSnapshot | null;
  status: ConnectionStatus;
  lastUpdatedAt: Date | null;
  error: string | null;
  reconnect: () => void;
}

function readCachedSnapshot(): OpsSnapshot | null {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const snapshot = JSON.parse(raw) as OpsSnapshot;
    const age = Date.now() - Date.parse(snapshot.generatedAt);
    return Number.isFinite(age) && age < CACHE_MAX_AGE_MS ? snapshot : null;
  } catch {
    return null;
  }
}

function writeCachedSnapshot(snapshot: OpsSnapshot): void {
  try {
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(snapshot));
  } catch {
    // Storage full or unavailable; the next poll repaints anyway.
  }
}

class OpsAccessError extends Error {}

async function fetchSnapshot(signal: AbortSignal): Promise<OpsSnapshot> {
  if (!getAccessToken) throw new OpsAccessError('Not authenticated');
  let token = '';
  try {
    token = await getAccessToken();
  } catch {
    throw new OpsAccessError('Not authenticated');
  }
  if (!token) throw new OpsAccessError('Not authenticated');
  const response = await fetch(`${BASE_URL}/api/admin/ops/snapshot`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    signal,
  });
  if (response.status === 401 || response.status === 403) throw new OpsAccessError('Access denied');
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return (await response.json()) as OpsSnapshot;
}

export function useOpsSnapshot(): UseOpsSnapshotResult {
  const [snapshot, setSnapshot] = useState<OpsSnapshot | null>(readCachedSnapshot);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const failuresRef = useRef(0);
  const stoppedRef = useRef(false);

  const poll = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (stoppedRef.current || controllerRef.current) return;
    const controller = new AbortController();
    controllerRef.current = controller;
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const next = await fetchSnapshot(controller.signal);
      if (stoppedRef.current) return;
      failuresRef.current = 0;
      setSnapshot(next);
      setLastUpdatedAt(new Date());
      setStatus('live');
      setError(null);
      writeCachedSnapshot(next);
    } catch (pollError) {
      if (stoppedRef.current) return;
      if (pollError instanceof OpsAccessError) {
        setStatus('offline');
        setError(pollError.message);
        return;
      }
      failuresRef.current += 1;
      setStatus('reconnecting');
      setError(controller.signal.aborted ? 'The ops snapshot took too long' : pollError instanceof Error ? pollError.message : 'Request failed');
    } finally {
      clearTimeout(timeout);
      controllerRef.current = null;
    }
    if (stoppedRef.current || document.visibilityState === 'hidden') return;
    timerRef.current = setTimeout(() => void poll(), nextPollDelay(failuresRef.current));
  }, []);

  const reconnect = useCallback(() => {
    failuresRef.current = 0;
    setStatus('connecting');
    void poll();
  }, [poll]);

  useEffect(() => {
    stoppedRef.current = false;
    void poll();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void poll();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stoppedRef.current = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (timerRef.current) clearTimeout(timerRef.current);
      controllerRef.current?.abort();
    };
  }, [poll]);

  return { snapshot, status, lastUpdatedAt, error, reconnect };
}
